import React, { useState } from "react";
import { useAtomValue } from "jotai";
import {
  FiClock,
  FiCheckCircle,
  FiChevronDown,
  FiChevronRight,
  FiXOctagon,
} from "react-icons/fi";

import Markdown from "../Markdown";
import TaskStatus from "./TaskStatus";
import styles from "./Message.module.scss";
import { parseLongTaskResult } from "utilities/longTaskResult";
import { longTaskGroupsAtom } from "hooks/tasks/useLongTaskGroups";

interface ToolCallDetailsProps {
  toolName: string;
  toolInput: Record<string, unknown>;
  /**
   * The `content` of a tool call message.
   * Tool calls rarely have content.
   */
  toolCallContent?: string;
  /**
   * The `content` of a tool result message.
   * e.g. "\nThis Afternoon:\nTemperature: 73..."
   */
  toolResult?: string;
  /**
   * Will be `true` if the tool call failed.
   */
  isError?: boolean;
  /**
   * If the tool result corresponds to an async backend task, this is the task's
   * jobId. For an MCP task the row's jobId is its API `task_id` (the API
   * field this is sourced from), so the two are equal.
   */
  jobId?: string;
  /**
   * The MCP server that provides this tool. Null if not an MCP tool.
   */
  mcpServer: string | null;
  onAskAboutTask?: (prompt: string) => void;
  incognito?: boolean;
}

const MCP_SERVER_DISPLAY_NAMES: Record<string, string> = {
  trino_query_assistant: "Trino Query Assistant",
  galileo_mcp: "Galileo",
  wallipedia: "Wallipedia",
  btsat_mcp: "BTSAT",
  evman_mcp: "EvMan",
  agnlp_mcp: "AgNLP",
  gaia_mcp: "Gaia",
  skyfall_mcp: "Skyfall",
  sfa_mcp: "SFA",
  synteny_mcp: "Synteny",
  cc_file_api: "CC File API",
  executeCode: "Code Execution",
  media_mcp: "Media API",
  midas_mcp: "Midas",
  datahub_mcp: "DataHub",
};

/**
 * Renders a tool call as a customized <details> element.
 */
export default React.memo(function ToolCallDetails({
  toolName,
  toolInput,
  toolCallContent,
  toolResult,
  isError,
  jobId,
  mcpServer,
  onAskAboutTask,
  incognito,
}: ToolCallDetailsProps) {
  const toolCallInProgress = toolResult === undefined;
  const [isOpen, setIsOpen] = useState(false);
  // Read the lifecycle map from the atom, not the module mirror: this component
  // is memoized and renders before the tool-metadata fetch resolves, so a
  // render-time read of the mutable mirror would never see the hydrated map.
  const groupMap = useAtomValue(longTaskGroupsAtom);

  const ChevronIcon = isOpen ? FiChevronDown : FiChevronRight;

  const toolLabel = (
    <>
      <span className="i">{toolName}</span>
      {mcpServer && (
        <span className={styles.toolCallMcpServer}>
          {" "}
          · {MCP_SERVER_DISPLAY_NAMES[mcpServer] ?? mcpServer}
        </span>
      )}
    </>
  );

  let _detailsLabel = (
    <>
      <FiClock className={styles.toolCallIcon} />
      <span>Calling {toolLabel}</span>
    </>
  );
  if (isError) {
    _detailsLabel = (
      <>
        <FiXOctagon
          className={`${styles.toolCallIcon} ${styles.toolCallErrorIcon}`}
        />
        <span>Error calling {toolLabel}</span>
      </>
    );
  } else if (!toolCallInProgress) {
    _detailsLabel = (
      <>
        <FiCheckCircle
          className={`${styles.toolCallIcon} ${styles.toolCallSuccessIcon}`}
        />
        <span>Called {toolLabel}</span>
      </>
    );
  }

  const detailsLabel = (
    <span className={styles.toolCallLabel}>
      <ChevronIcon className={styles.toolCallChevron} />
      {_detailsLabel}
    </span>
  );

  let bodyMarkdown = "";

  if (toolCallContent) {
    bodyMarkdown += `\n${toolCallContent}\n`;
  }

  // Attempt to pretty-print JSON results; fall back to raw text.
  let toolResultFormatted = toolResult;
  let toolResultExtension = "txt";
  if (toolResult !== undefined) {
    try {
      const toolResultJSON = JSON.parse(toolResult);
      toolResultFormatted = JSON.stringify(toolResultJSON, null, 2);
      toolResultExtension = "json";
    } catch {
      // pass
    }
  }

  bodyMarkdown += `**Input:**

\`\`\`json
${JSON.stringify(toolInput, null, 2)}
\`\`\`\n`;

  if (toolResult !== undefined) {
    bodyMarkdown += `**Output:**

\`\`\`${toolResultExtension}
${toolResultFormatted}
\`\`\``;
  }

  // Render a long-task card for an inline submit result (no task handle).
  let inlineSubmitCard: React.ReactNode = null;
  if (!jobId && toolResult !== undefined) {
    const binding = groupMap.byToolName[toolName];
    const parsed =
      binding?.role === "submit"
        ? parseLongTaskResult(toolResult, "submit")
        : null;
    if (parsed) {
      inlineSubmitCard = (
        <TaskStatus
          jobId={parsed.job_id}
          toolName={toolName}
          kind="long-task"
          onAskAboutTask={onAskAboutTask}
          incognito={incognito}
        />
      );
    }
  }

  return (
    <>
      <details
        className={styles.toolCallDetails}
        open={isOpen}
        onToggle={(e) => setIsOpen((e.target as HTMLDetailsElement).open)}
      >
        <summary className={styles.toolCallSummary}>{detailsLabel}</summary>
        <div className={styles.toolCallContent}>
          <Markdown content={bodyMarkdown} />
        </div>
      </details>
      {/* Suppress the MCP-task TaskStatus card for a long-task fetch tool: its
          result surfaces on the owning job card instead (keyed by job_id),
          avoiding two cards per job. Routed by role from the lifecycle map. */}
      {jobId && groupMap.byToolName[toolName]?.role !== "fetch" && (
        <TaskStatus
          jobId={jobId}
          toolName={toolName}
          onAskAboutTask={onAskAboutTask}
          incognito={incognito}
        />
      )}
      {inlineSubmitCard}
    </>
  );
});
